import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Btn, DataSizeBar, fmtBytes, fmtNumber, Section, StatCard, TypeBadge } from './parts';
import { ChunkTable, Chunk } from './ChunkTable';
import { useToast } from '@/lib/ui';
import { useAuth } from '@/hooks/useAuth';
import { useConfirmedMutation } from '@/api/mutations';
import { caggMatOnlyFn, caggOptimizeFn } from '@/api/endpoints';

const TARGET_DEFAULT = 256;
const TARGET_MIN = 128;
const TARGET_MAX = 1024;

export type CaggObj = {
  schema: string;
  name: string;
  type: 'cagg';
  source_hypertable: string;
  on_disk_bytes: number;
  decompressed_bytes: number;
  compression_pct: number;
  rows: number;
  chunks_count: number;
  chunk_interval: string;
  materialized_only: boolean;
  policies?: string[];
};

export function DetailCagg({ obj, chunks }: { obj: CaggObj; chunks: Chunk[] }) {
  const toast = useToast();
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const isSuperAdmin = me?.role === 'superAdmin';
  const fqn = `${obj.schema}.${obj.name}`;
  const matOnly = obj.materialized_only;
  const invalidate = () => qc.invalidateQueries({ queryKey: ['db'] });

  const [optimizeOpen, setOptimizeOpen] = useState(false);
  const [targetMb, setTargetMb] = useState(TARGET_DEFAULT);
  const targetValid = Number.isInteger(targetMb) && targetMb >= TARGET_MIN && targetMb <= TARGET_MAX;

  const matOnlyMut = useConfirmedMutation(
    (enabled: boolean) => caggMatOnlyFn(obj.schema, obj.name, enabled),
    {
      title: 'materialized_only',
      message: 'Confirmer le changement du mode materialized_only ?',
      destructive: true,
      onSuccess: () => { toast.push('materialized_only mis à jour', 'success'); invalidate(); },
    },
  );

  const optimizeMut = useConfirmedMutation(
    (mb: number) => caggOptimizeFn(obj.schema, obj.name, mb),
    {
      title: `Optimiser les chunks de ${fqn}`,
      message: `Fusion/division des chunks matérialisés vers ~${targetMb} Mo. Tâche mise en file (queue ingest), exécutée en série avec l'ingestion. Confirmer ?`,
      onSuccess: () => {
        toast.push('Optimisation des chunks mise en file', 'success');
        setOptimizeOpen(false);
        invalidate();
      },
      // The response interceptor already toasts the server message; only close
      // the dialog, otherwise it stays up behind the toast and the user retries
      // in a loop. A rejected confirm keeps it open — that is a deliberate abort.
      onError: (e) => {
        if (e.message === 'ConfirmRejected') return;
        setOptimizeOpen(false);
      },
    },
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          <Link to="/db" className="text-sm text-muted-foreground hover:text-foreground">
            ← Retour
          </Link>
          <h2 className="text-xl font-bold font-mono">{fqn}</h2>
          <TypeBadge type={obj.type} />
          <span className="text-xs text-muted-foreground">
            source: <code className="font-mono">{obj.source_hypertable}</code>
          </span>
        </div>
        <div className="flex items-center gap-2">
          {isSuperAdmin && (
            <Btn size="sm" variant="primary" onClick={() => setOptimizeOpen(true)}>
              Optimize chunks
            </Btn>
          )}
          <span className="text-xs text-muted-foreground">Refresh via « Actions globales » (queue ingest)</span>
        </div>
      </div>

      <Section title="Vue d'ensemble">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard label="On-disk" value={fmtBytes(obj.on_disk_bytes)} tone="primary" sub="∑ chunks mat. hypertable" />
          <StatCard label="Décompressé" value={fmtBytes(obj.decompressed_bytes)} />
          <StatCard label="Compression" value={`${obj.compression_pct}%`} tone="success" />
          <StatCard label="Lignes" value={fmtNumber(obj.rows)} />
          <StatCard label="Chunks" value={obj.chunks_count} sub={`intervalle ${obj.chunk_interval}`} />
          <StatCard label="Policies" value={obj.policies?.length ?? 0} sub={obj.policies?.join(' · ')} />
        </div>
      </Section>

      <Section title="Materialized only">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-sm font-medium">Statut :</span>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${
                  matOnly
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
                    : 'bg-slate-100 text-slate-700 dark:bg-slate-700/40 dark:text-slate-200'
                }`}
              >
                {matOnly ? 'ON' : 'OFF'}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {matOnly
                ? 'Les requêtes lisent uniquement les données matérialisées. Plus rapide mais peut manquer les données récentes non rafraîchies.'
                : 'Les requêtes fusionnent données matérialisées + données brutes (real-time aggregation). Toujours à jour mais plus lent.'}
            </p>
          </div>
          <button
            onClick={() => matOnlyMut.mutate(!matOnly)}
            disabled={!isSuperAdmin}
            className={`relative inline-flex h-7 w-14 items-center rounded-full transition disabled:opacity-50 disabled:cursor-not-allowed ${
              matOnly ? 'bg-green-500' : 'bg-slate-400'
            }`}
            aria-label="Toggle materialized_only"
            title={isSuperAdmin ? 'Toggle materialized_only' : 'superAdmin requis'}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                matOnly ? 'translate-x-8' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
        <p className="text-[11px] text-muted-foreground mt-3">
          Modification restreinte <strong>superAdmin</strong>.
        </p>
      </Section>

      <Section title="Comparaison taille">
        <DataSizeBar originalBytes={obj.decompressed_bytes} onDiskBytes={obj.on_disk_bytes} />
      </Section>

      <Section title={`Chunks matérialisés (${chunks.length})`}>
        <ChunkTable chunks={chunks} hypertable={fqn} />
      </Section>

      {optimizeOpen && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/50" onClick={() => setOptimizeOpen(false)}>
          <div className="bg-card border border-border rounded-lg shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-border">
              <h3 className="text-lg font-bold">Optimiser les chunks (CAGG)</h3>
              <p className="text-xs text-muted-foreground mt-1 font-mono">{fqn}</p>
            </div>
            <div className="px-5 py-4 space-y-2">
              <label className="text-sm font-medium" htmlFor="cagg-optimize-target-mb">Taille cible par chunk (Mo)</label>
              <input
                id="cagg-optimize-target-mb"
                type="number"
                min={TARGET_MIN}
                max={TARGET_MAX}
                step={64}
                value={targetMb}
                onChange={(e) => setTargetMb(Math.trunc(Number(e.target.value)))}
                className="w-full h-9 px-3 text-sm rounded-md border border-input bg-background font-mono"
              />
              <p className="text-xs text-muted-foreground">
                Les chunks matérialisés seront fusionnés/divisés pour viser ~{targetMb} Mo. Bornes {TARGET_MIN}–{TARGET_MAX} Mo.
                Tâche exécutée dans la file ingest (en série avec l'ingestion).
              </p>
              {!targetValid && (
                <p className="text-xs text-destructive">Valeur hors bornes ({TARGET_MIN}–{TARGET_MAX} Mo, entier).</p>
              )}
            </div>
            <div className="px-5 py-3 border-t border-border flex justify-end gap-2">
              <Btn onClick={() => setOptimizeOpen(false)}>Annuler</Btn>
              <Btn variant="primary" onClick={() => optimizeMut.mutate(targetMb)} disabled={!targetValid || optimizeMut.isPending}>
                {optimizeMut.isPending ? 'Envoi…' : 'Optimiser (superAdmin)'}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
